import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Wallet, CreditCard, IndianRupee, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WalletTopup({ open, onOpenChange, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ min_topup_amount: 500, key_id: '' });
  const [step, setStep] = useState('input'); // input, processing, success, error

  const quickAmounts = [500, 1000, 2000, 5000, 10000, 25000];

  useEffect(() => {
    if (open) {
      fetchConfig();
      setStep('input');
      setAmount('');
    }
  }, [open]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount < config.min_topup_amount) {
      toast.error(`Minimum top-up amount is ₹${config.min_topup_amount}`);
      return;
    }

    setLoading(true);
    setStep('processing');

    try {
      // Load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        amount: numAmount,
        purpose: 'wallet_topup'
      });

      const orderData = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: 'CloudSetu',
        description: 'Wallet Top-up',
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              setStep('success');
              toast.success(`₹${numAmount} added to wallet successfully!`);
              if (onSuccess) {
                onSuccess(verifyResponse.data);
              }
              setTimeout(() => {
                onOpenChange(false);
              }, 2000);
            }
          } catch (error) {
            setStep('error');
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: orderData.user?.name || '',
          email: orderData.user?.email || '',
          contact: orderData.user?.phone || ''
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: () => {
            setStep('input');
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setStep('error');
        toast.error(`Payment failed: ${response.error.description}`);
      });
      razorpay.open();

    } catch (error) {
      setStep('error');
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-blue-600" />
            Top-up Wallet
          </DialogTitle>
          <DialogDescription>
            Add funds to your wallet using Razorpay
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-6 py-4">
            {/* Quick Amount Buttons */}
            <div>
              <Label className="text-sm text-slate-600 mb-2 block">Quick Select</Label>
              <div className="grid grid-cols-3 gap-2">
                {quickAmounts.map((amt) => (
                  <Button
                    key={amt}
                    variant={parseFloat(amount) === amt ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAmount(amt.toString())}
                    className="text-sm"
                  >
                    ₹{amt.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Amount Input */}
            <div className="space-y-2">
              <Label>Enter Amount</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  min={config.min_topup_amount}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Min ₹${config.min_topup_amount}`}
                  className="pl-10 text-lg"
                  data-testid="topup-amount"
                />
              </div>
              <p className="text-xs text-slate-500">
                Minimum top-up: ₹{config.min_topup_amount}
              </p>
            </div>

            {/* Payment Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
                <CreditCard className="h-4 w-4" />
                Secure Payment via Razorpay
              </div>
              <p className="text-sm text-blue-700">
                Supports UPI, Credit/Debit Cards, Net Banking, and Wallets
              </p>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium">Processing Payment...</p>
            <p className="text-sm text-slate-500 mt-1">Please complete the payment in Razorpay window</p>
          </div>
        )}

        {step === 'success' && (
          <div className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-green-700">Payment Successful!</p>
            <p className="text-sm text-slate-500 mt-1">₹{amount} has been added to your wallet</p>
          </div>
        )}

        {step === 'error' && (
          <div className="py-12 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-red-700">Payment Failed</p>
            <p className="text-sm text-slate-500 mt-1">Please try again</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setStep('input')}
            >
              Try Again
            </Button>
          </div>
        )}

        {step === 'input' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePayment}
              disabled={loading || !amount || parseFloat(amount) < config.min_topup_amount}
              data-testid="proceed-payment"
            >
              {loading ? 'Processing...' : `Pay ₹${amount || 0}`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
